// Como um fornecedor aparece nas listas do financeiro — em UM lugar só.
//
// O problema (Lucas, 31/08/2026): "se tenho mais de uma filial de um cliente com
// a mesma razão social, acabo sem saber qual selecionar". Uma empresa com vários
// CNPJs — um por empreendimento, como a Consplan — vira N entradas idênticas na
// lista, e escolher a errada joga a despesa no CNPJ errado.
//
// O rótulo tinha CNPJ desde sempre, mas 939 dos 951 fornecedores estão SEM CNPJ
// cadastrado (medido em 31/08/2026) — então na prática ele nunca aparecia. Por
// isso este arquivo anda junto com duas outras coisas: a busca por CNPJ na
// Receita ao cadastrar (que preenche o campo) e o botão de editar fornecedor
// (que conserta quem já está sem).
//
// Existiam cinco cópias do mesmo `label:` espalhadas pelas telas; qualquer
// melhoria tinha que ser feita cinco vezes. Agora é aqui.

export interface FornecedorSlim {
  id: string;
  nome_fantasia: string;
  razao_social?: string | null;
  cnpj_cpf?: string | null;
}

/** Máscara de CNPJ (14 díg.) ou CPF (11). Qualquer outra coisa volta como veio. */
export function formatarCnpjCpf(valor?: string | null): string {
  const d = String(valor || '').replace(/\D/g, '');
  if (d.length === 14) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
  if (d.length === 11) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
  return String(valor || '');
}

/**
 * O que a pessoa lê na lista: nome fantasia · CNPJ — razão social.
 * A razão social só entra quando diz algo novo (não repete o fantasia), e o
 * aviso de "sem CNPJ" é explícito: numa lista com dois nomes iguais, saber que
 * NENHUM dos dois tem CNPJ é a informação que faz procurar o cadastro.
 */
export function rotuloFornecedor(f: FornecedorSlim, opts: { avisarSemCnpj?: boolean } = {}): string {
  const fantasia = (f.nome_fantasia || '').trim();
  const razao = (f.razao_social || '').trim();
  const cnpj = formatarCnpjCpf(f.cnpj_cpf);
  let s = fantasia || razao || '(sem nome)';
  if (cnpj) s += ` · ${cnpj}`;
  else if (opts.avisarSemCnpj) s += ' · sem CNPJ';
  // Só acrescenta a razão quando ela é a SEGUNDA informação. Sem fantasia
  // cadastrado a razão já é o nome principal — repetir daria "ACME — ACME".
  if (fantasia && razao && razao.toLowerCase() !== fantasia.toLowerCase()) s += ` — ${razao}`;
  return s;
}

/**
 * Texto que a busca varre: nome e razão social. Os dígitos do CNPJ ficam FORA
 * daqui de propósito — juntos, digitar "0" casava toda empresa cujo CNPJ tem
 * um zero, que é praticamente a lista inteira.
 */
export function buscaFornecedor(f: FornecedorSlim): string {
  return [f.nome_fantasia, f.razao_social].filter(Boolean).join(' ').toLowerCase();
}

/** Só os dígitos do CNPJ/CPF — a outra metade da busca. */
export function digitosFornecedor(f: FornecedorSlim): string {
  return String(f.cnpj_cpf || '').replace(/\D/g, '');
}

export interface OpcaoFornecedor {
  label: string;
  value: string;
  _busca: string;
  _digitos: string;
}

/**
 * Opções do <Select> de fornecedor.
 * @param valorPorNome usa o nome fantasia como `value` (o filtro da listagem do
 *        CP filtra por texto, não por id). Nesse modo nomes repetidos viram UMA
 *        opção só — de propósito: o filtro é por nome mesmo.
 */
export function opcoesFornecedor(
  lista: FornecedorSlim[],
  opts: { valorPorNome?: boolean; avisarSemCnpj?: boolean } = {},
): OpcaoFornecedor[] {
  const vistos = new Set<string>();
  const out: OpcaoFornecedor[] = [];
  for (const f of lista || []) {
    const value = opts.valorPorNome ? (f.nome_fantasia || '') : String(f.id);
    if (!value) continue;
    if (vistos.has(value)) continue;   // antd repete a chave e o React reclama
    vistos.add(value);
    out.push({
      label: rotuloFornecedor(f, { avisarSemCnpj: opts.avisarSemCnpj }),
      value,
      _busca: buscaFornecedor(f),
      _digitos: digitosFornecedor(f),
    });
  }
  return out;
}

export interface OpcaoFornecedorTexto extends OpcaoFornecedor {
  key: string;
  _id: string;
}

/**
 * Opções do <AutoComplete> de fornecedor — "escolhe do cadastro OU digita".
 *
 * Diferente do <Select>: o AutoComplete escreve o `value` DENTRO do campo de
 * texto, então o value tem que ser o nome, não o id. E aqui nome repetido NÃO
 * é deduplicado (o `valorPorNome` do opcoesFornecedor deduplica): são
 * justamente as filiais de mesma razão social, o motivo de este arquivo
 * existir. Por isso a chave do React é o id, em `key`, e o id escolhido volta
 * por `_id` no onSelect.
 */
export function opcoesFornecedorTexto(lista: FornecedorSlim[]): OpcaoFornecedorTexto[] {
  return (lista || [])
    .filter(f => (f.nome_fantasia || '').trim())
    .map(f => ({
      key: String(f.id),
      _id: String(f.id),
      value: f.nome_fantasia,
      label: rotuloFornecedor(f, { avisarSemCnpj: true }),
      _busca: buscaFornecedor(f),
      _digitos: digitosFornecedor(f),
    }));
}

/**
 * Filtro do <Select>: casa por nome, razão social OU dígitos do CNPJ.
 * Digitar "0001" tem que achar a filial; `optionFilterProp="label"` sozinho não
 * acha, porque a pessoa digita sem máscara e o rótulo está com máscara.
 * Exige 3+ dígitos pra "1" não casar meia lista — mesma régua do SelectCliente.
 */
export function filtrarFornecedor(
  input: string,
  option?: { _busca?: string; _digitos?: string; label?: string },
): boolean {
  const q = String(input || '').toLowerCase().trim();
  if (!q) return true;
  const alvo = (option?._busca || String(option?.label || '').toLowerCase());
  if (alvo.includes(q)) return true;
  const digitos = q.replace(/\D/g, '');
  return digitos.length >= 3 && String(option?._digitos || '').includes(digitos);
}
